import { useEffect, useState, useCallback } from 'react';
import { adminAPI } from '@/lib/adminApi';
import { Plus, Trash2, Link as LinkIcon, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = ['debloat', 'asymmetry', 'coloring', 'ratios', 'physique', 'styling', 'status'];

export default function AdminAffiliatesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', url: '', category: 'debloat', note: '', image_url: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminAPI.listAffiliates(filter !== 'all' ? { category: filter } : {});
      setItems(r.data.affiliates);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name || !form.url) return toast.error('Name and URL required');
    try {
      await adminAPI.createAffiliate(form);
      toast.success('Added');
      setForm({ name: '', url: '', category: 'debloat', note: '', image_url: '' });
      setShowForm(false);
      load();
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this affiliate link?')) return;
    try {
      await adminAPI.deleteAffiliate(id);
      toast.success('Deleted');
      load();
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="p-8 max-w-6xl" data-testid="admin-affiliates-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: "'Manrope', sans-serif" }}>Affiliate Library</h1>
          <p className="text-purple-200/50 text-sm mt-1">Reusable product links for reports.</p>
        </div>
        <button onClick={() => setShowForm(s => !s)} className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-white text-sm font-medium" style={{ background: 'linear-gradient(145deg,#a855f7,#7c3aed)' }} data-testid="new-affiliate-btn">
          <Plus className="w-4 h-4" /> New Link
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="bg-white/[0.03] border border-white/5 rounded-2xl p-5 mb-6 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Product name" className="bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm" data-testid="aff-name-input" />
            <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm capitalize" data-testid="aff-category-select">
              {CATEGORIES.map(c => <option key={c} value={c} className="bg-purple-900">{c}</option>)}
            </select>
          </div>
          <input value={form.url} onChange={e => setForm({ ...form, url: e.target.value })} placeholder="https://amzn.to/…" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm" data-testid="aff-url-input" />
          <input value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} placeholder="Internal note (optional)" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm" />
          <input value={form.image_url} onChange={e => setForm({ ...form, image_url: e.target.value })} placeholder="Image URL (optional)" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm" />
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 rounded-lg text-purple-200/70 text-sm hover:bg-white/5">Cancel</button>
            <button type="submit" className="px-5 py-2 rounded-lg text-white text-sm font-medium" style={{ background: 'linear-gradient(145deg,#a855f7,#7c3aed)' }} data-testid="save-affiliate-btn">Save</button>
          </div>
        </form>
      )}

      <div className="flex gap-2 mb-4 flex-wrap">
        <button onClick={() => setFilter('all')} className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${filter === 'all' ? 'bg-purple-500 text-white' : 'bg-white/5 text-purple-200/60 hover:bg-white/10'}`}>All</button>
        {CATEGORIES.map(c => (
          <button key={c} onClick={() => setFilter(c)} className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize transition ${filter === c ? 'bg-purple-500 text-white' : 'bg-white/5 text-purple-200/60 hover:bg-white/10'}`}>
            {c}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {loading && <p className="text-purple-200/50 col-span-2">Loading…</p>}
        {!loading && items.length === 0 && (
          <div className="col-span-2 text-center py-16 text-purple-200/40">
            <LinkIcon className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p>No affiliate links yet. Add your first one.</p>
          </div>
        )}
        {items.map(item => (
          <div key={item.id} className="bg-white/[0.03] border border-white/5 rounded-xl p-4" data-testid={`aff-item-${item.id}`}>
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium text-sm truncate">{item.name}</p>
                <span className="inline-block mt-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-purple-500/20 text-purple-200">{item.category}</span>
              </div>
              <button onClick={() => handleDelete(item.id)} className="text-red-300/40 hover:text-red-300">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <a href={item.url} target="_blank" rel="noreferrer" className="text-xs text-purple-300 hover:text-purple-200 flex items-center gap-1 truncate">
              <ExternalLink className="w-3 h-3 flex-shrink-0" /> <span className="truncate">{item.url}</span>
            </a>
            {item.note && <p className="text-xs text-purple-200/50 mt-2">{item.note}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}
